import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useSpring } from "framer-motion";
import { HelixMotif } from "@/components/brand/MoleculeBg";
import { Reveal } from "@/components/motion/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Precision",
    body:
      "Every material is catalogued and identified with exacting care — because reproducible research begins with unambiguous inputs.",
  },
  {
    n: "02",
    title: "Clarity",
    body:
      "Organized, transparent product information — no ambiguity, no overstatement. What you see is what you receive.",
  },
  {
    n: "03",
    title: "Presentation",
    body:
      "A refined, professional experience from catalogue to careful packaging. Serious tools presented seriously.",
  },
  {
    n: "04",
    title: "Discretion",
    body:
      "Understated packaging and quiet logistics. We respect the sensitivity of laboratory workflows.",
  },
  {
    n: "05",
    title: "Focus",
    body:
      "Research use only — always. We keep our positioning honest so scientists can keep theirs.",
  },
];

export default function Manifesto() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  // subtle, buttery parallax on the ornamental helix + heading
  const helixY = useSpring(useTransform(scrollYProgress, [0, 1], [80, -80]), {
    stiffness: 60, damping: 20, mass: 0.6,
  });
  const helixRotate = useTransform(scrollYProgress, [0, 1], [-6, 6]);
  const headingY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      ref={ref}
      className="relative bg-[#111C32] overflow-hidden grain"
      aria-label="Manifesto"
      data-testid="manifesto"
    >
      {/* Ornamental helix + soft radial glow */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute right-[-4%] top-1/2 -translate-y-1/2 h-[130%] w-[42%] hidden md:block"
        style={reduce ? undefined : { y: helixY, rotate: helixRotate }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(200,169,106,0.16),transparent_60%)]" />
        <HelixMotif className="absolute inset-0 h-full w-full" color="#C8A96A" opacity={0.5} />
      </motion.div>

      <div className="container-rnv py-24 md:py-36 relative z-10">
        <motion.div style={reduce ? undefined : { y: headingY }}>
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C8A96A]" />
              <span className="overline !text-[#D8BE87]" data-testid="manifesto-eyebrow">
                The Renova Manifesto
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-[4.4rem] text-[#F7F4EF] leading-[1.05] max-w-4xl">
              Five principles.
              <span className="block text-[#C8A96A]/85 italic font-light">One standard of care.</span>
            </h2>
            <p className="mt-7 text-[#B9BCC4] text-lg leading-relaxed max-w-2xl">
              Renova Peptide Co. exists to make research materials easy to understand and
              confident to handle. These are the commitments we return to on every batch,
              every catalogue entry, every shipment.
            </p>
          </Reveal>
        </motion.div>

        <ol className="mt-16 md:mt-24 relative">
          {/* vertical gilded rule */}
          <span
            aria-hidden="true"
            className="absolute left-[22px] md:left-[34px] top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-[#C8A96A]/60 to-transparent"
          />
          {CHAPTERS.map((c, i) => (
            <motion.li
              key={c.n}
              initial={reduce ? false : { opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.85, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="relative grid grid-cols-[56px_1fr] md:grid-cols-[80px_1fr] gap-6 md:gap-10 py-8 md:py-10 border-b border-white/8 last:border-none"
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <div className="relative">
                <span className="absolute left-0 top-1 h-3 w-3 rounded-full bg-[#C8A96A] shadow-[0_0_0_5px_rgba(200,169,106,0.16)]" />
                <span className="block pl-8 md:pl-12 font-serif text-5xl md:text-6xl text-[#C8A96A]/80 leading-none tabular-nums">
                  {c.n}
                </span>
              </div>
              <div>
                <h3 className="font-serif text-2xl md:text-4xl text-[#F7F4EF] leading-tight">
                  {c.title}
                </h3>
                <p className="mt-3 md:mt-4 text-[#9aa0b0] leading-relaxed max-w-2xl md:text-lg">
                  {c.body}
                </p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
